import type { AdminDemoAccount, AdminDemoClub, DemoSport } from "@teamna/shared";
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  Badge,
  Button,
  Callout,
  Card,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  PageHeader,
  SkeletonList,
  showToast,
} from "@teamna/ui/components/index.ts";
import { formatCountdown, formatDateTime } from "@teamna/web-client/lib/format.ts";
import { setActingAs } from "../lib/act-as.ts";
import {
  useDemoClubs,
  useDestroyDemoClub,
  useExtendDemoClub,
  useSendDemoLink,
  useSpawnDemoClub,
  useStaffMe,
} from "../lib/cc-api.ts";
import { countText, sportName } from "./format.ts";

/**
 * Demo clubs: spawn a whole club to show Teamna to a prospect, and destroy it after.
 *
 * One click makes a club that is set up and in use (timetable, packages, members, balances,
 * bookings, a message) with three logins: the owner, a coach and a parent. "Send sign-in link"
 * emails a one-time link for a login to a Teamna team address; the link opens Desk signed in as
 * that login. A club is destroyed after seven days unless someone keeps it longer.
 */

const ROLE_NAMES: Record<AdminDemoAccount["role"], string> = {
  owner: "Owner",
  coach: "Coach",
  parent: "Parent",
};

const ROLE_HINTS: Record<AdminDemoAccount["role"], string> = {
  owner: "Opens the owner office: the whole club",
  coach: "Desk has no coach area yet, so this opens an empty member schedule",
  parent: "Opens the member area: children, balance and bookings",
};

function AccountRow({ club, account, sendTo }: { club: AdminDemoClub; account: AdminDemoAccount; sendTo: string }) {
  const send = useSendDemoLink();
  // Only a local run with no email provider answers with the link itself.
  const [localUrl, setLocalUrl] = useState<string | null>(null);

  const sendLink = () => {
    send.mutate(
      { businessId: club.businessId, role: account.role, to: sendTo.trim() },
      {
        onSuccess: (result) => {
          setLocalUrl(result.url ?? null);
          showToast(
            result.url
              ? "Email is not configured here. The link is under the login."
              : `Link sent to ${result.sentTo}. It works one time, for 15 minutes.`,
          );
        },
        onError: (error) => showToast(error.message || "Could not send the link. Try again.", { tone: "info" }),
      },
    );
  };

  return (
    <li className="px-4 py-3">
      <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center sm:gap-4">
        <div className="min-w-0">
          <p className="font-semibold text-ink">
            {ROLE_NAMES[account.role]} <span className="font-normal text-ink-3">{account.name}</span>
          </p>
          <p className="text-[12px] text-ink-3">{ROLE_HINTS[account.role]}</p>
        </div>
        <Button
          variant="secondary"
          onClick={sendLink}
          loading={send.isPending}
          aria-label={`Send sign-in link for the ${ROLE_NAMES[account.role].toLowerCase()} of ${club.name}`}
        >
          Send sign-in link
        </Button>
      </div>
      {localUrl ? (
        <p className="mt-2 text-[12px] text-ink-2 [overflow-wrap:anywhere]">
          <a className="text-accent-strong underline" href={localUrl}>
            {localUrl}
          </a>
        </p>
      ) : null}
    </li>
  );
}

function DemoClubCard({ club, sendTo }: { club: AdminDemoClub; sendTo: string }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const extend = useExtendDemoClub();
  const destroy = useDestroyDemoClub();
  const [confirming, setConfirming] = useState(false);

  const actAsOwner = () => {
    setActingAs({ businessId: club.businessId, businessName: club.name });
    // Owner data from a previous act-as must never paint for this business.
    queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== "cc" });
    void navigate({ to: "/owner/home" });
  };

  const keep = () => {
    extend.mutate(club.businessId, {
      onSuccess: () => showToast(`${club.name} stays for 7 more days`),
      onError: () => showToast("Could not keep the club longer. Try again.", { tone: "info" }),
    });
  };

  const remove = () => {
    destroy.mutate(club.businessId, {
      onSuccess: () => {
        setConfirming(false);
        showToast(`${club.name} is destroyed`);
      },
      onError: () => showToast("Could not destroy the club. Try again.", { tone: "info" }),
    });
  };

  return (
    <Card>
      <div className="flex flex-col gap-3 border-b border-line px-4 py-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-[17px] font-semibold text-ink">{club.name}</h2>
            <Badge>{sportName(club.sport)}</Badge>
          </div>
          <p className="mt-1 text-[13px] text-ink-3">
            Destroyed {formatCountdown(club.expiresAt)} ({formatDateTime(club.expiresAt)}). Made by{" "}
            {club.createdByStaffEmail}. {countText(club.extraFamilies, "other family", "other families")} in the club.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" onClick={actAsOwner} aria-label={`Act as owner, ${club.name}`}>
            Act as owner
          </Button>
          <Button variant="secondary" onClick={keep} loading={extend.isPending} aria-label={`Keep 7 more days, ${club.name}`}>
            Keep 7 more days
          </Button>
          <Button variant="danger" onClick={() => setConfirming(true)} aria-label={`Destroy ${club.name}`}>
            Destroy
          </Button>
        </div>
      </div>
      <ul className="divide-y divide-line">
        {club.accounts.map((account) => (
          <AccountRow key={account.userId} club={club} account={account} sendTo={sendTo} />
        ))}
      </ul>
      <ConfirmDialog
        open={confirming}
        title={`Destroy ${club.name}?`}
        confirmLabel="Destroy"
        tone="danger"
        loading={destroy.isPending}
        onConfirm={remove}
        onCancel={() => setConfirming(false)}
      >
        The club, its three logins, the other families and all their data are deleted. A browser that is
        signed in to one of these accounts is signed out, and links not used yet stop working. You
        cannot undo this.
      </ConfirmDialog>
    </Card>
  );
}

export function DemoClubsScreen() {
  const clubs = useDemoClubs();
  const spawn = useSpawnDemoClub();
  const staff = useStaffMe();
  const [spawning, setSpawning] = useState<DemoSport | null>(null);
  // Null until someone edits it, so it follows the signed-in staff member's address by default.
  const [sendToInput, setSendToInput] = useState<string | null>(null);
  const sendTo = sendToInput ?? staff.data?.email ?? "";

  const start = (sport: DemoSport) => {
    setSpawning(sport);
    spawn.mutate(sport, {
      onSuccess: (club) => showToast(`${club.name} is ready`),
      onError: (error) => showToast(error.message || "Could not make the demo club. Try again.", { tone: "info" }),
      onSettled: () => setSpawning(null),
    });
  };

  const actions = (
    <>
      <Button onClick={() => start("swimming")} loading={spawning === "swimming"} disabled={spawn.isPending}>
        New swimming club
      </Button>
      <Button variant="secondary" onClick={() => start("pilates")} loading={spawning === "pilates"} disabled={spawn.isPending}>
        New pilates club
      </Button>
    </>
  );

  const items = clubs.data ?? [];

  return (
    <>
      <PageHeader
        title="Demo clubs"
        description="Make a club with an owner, a coach and a parent to show Teamna. Each club is destroyed after 7 days."
        actions={actions}
      />
      <div className="mb-5">
        <Callout title="How to sign in">
          Press "Send sign-in link" on a login. Teamna emails a link to the address below. Open the link and
          Desk signs you in as that login. A link works one time and expires after 15 minutes.
        </Callout>
      </div>
      <div className="mb-5 max-w-[420px]">
        <Field
          label="Send links to"
          type="email"
          autoComplete="off"
          value={sendTo}
          onChange={(event) => setSendToInput(event.target.value)}
          hint="A @teamna.app address."
        />
      </div>
      {clubs.error && !clubs.data ? (
        <ErrorState error={clubs.error} onRetry={() => void clubs.refetch()} />
      ) : clubs.isLoading ? (
        <SkeletonList />
      ) : items.length === 0 ? (
        <EmptyState
          title="No demo clubs"
          message="Make one with the buttons above. It takes a few seconds."
        />
      ) : (
        <div className="flex flex-col gap-4">
          {items.map((club) => (
            <DemoClubCard key={club.businessId} club={club} sendTo={sendTo} />
          ))}
        </div>
      )}
    </>
  );
}
